<template>
  <config-base class="opc-ze" :saas-mode="saasMode" show-plan-details v-bind="$props" >
    <template slot="config">
      <!-- consumption mode -->
      <component
        v-if="saasMode === EventModeMap.PAY_PER_ATTENDEE"
        :ref="EventModeMap.PAY_PER_ATTENDEE"
        :plan-type="planType"
        :is='configConsumptionRowName'
      />
      <!-- subscription mode -->
      <saas-capacity-config-list
        v-if="saasMode === EventModeMap.SUBSCRIPTION"
        :ref="EventModeMap.SUBSCRIPTION"
        :configRowName="configRowName"
        :config-plan-type="planType"
        :config-capacity-key="capacityKey"
        :title="title"
      />
    </template>

    <template #footer v-if="isShowRemoveBtn">
      <zm-button type="link" ghost @click="removePlanByKey">
        {{ $t('billing.common.remove') }}
      </zm-button>
    </template>
  </config-base>
</template>

<script type="text/jsx">
import _ from 'lodash';
import { mapGetters, mapMutations } from 'vuex';
import ConfigList from '@/components/capacity/config-list';
import {
  BILLING_CYCLE,
  planTypes,
  ZOOM_SASS_ADDITIONAL_CATEGORIES
} from '@/common/constants';
import plansMixin from '@/plans/mx/plan';
import ConfigBase from '@/plans/EVENT_SAAS/config/base';
import planCommonMixin from '@/plans/EVENT_SAAS/mixin';
import { EventModeMap } from '@/plans/EVENT_SAAS/helper/constant';
import ConfigConsumptionRow from '@/plans/EVENT_SAAS/component/config-consumption-row';
import { pageConfig } from '@/common/service/page-config';
import { getPlanTotalLicense } from '@/common/service/validate';
import { getPlanTypeName } from '@/common/service/parse/parse-product-name';
import autoAddMixin from "@/components/capacity/mx/autoAdd";
import { enableNewUIForZS } from '@/common/service/op-switch';
import TwoWayControl from '@/plans/EVENT_SAAS/mixins/two-way-control';
import ConfigConsumptionRowV2 from '@/plans/EVENT_SAAS/component/config-consumption-row-v2';
import { isFromShoppingCart, isProductCanRemove } from '@/views/config/shopping-cart/persist/plan-remove';

const SaasCapacityConfigList = {
  mixins: [ConfigList ,autoAddMixin, enableNewUIForZS ? TwoWayControl : {}],
  props: {
    title: String,
  },
  data() {
    return {
      handleAutoAddMap: {}
    }
  },
  computed: {
    ...mapGetters(['basePlanTotalLicenses', 'getCurrentBasePlanInfo', 'getSelectedPlans']),
    // all plans under the current product type
    planList() {
      return this.planMapFlat.filter((p) => p.type === this.configPlanType && p.isSaas);
    },
    // all selected plans under the current product type
    selectedPlanList() {
      return this.getTypeGroupSelectedPlans[this.configPlanType]?.filter((p) => p.isSaas);
    },
    currentBasePlan() {
      return _.get(this.getCurrentBasePlanInfo, ["plans", 0], {});
    },
    mx_capacityTips() {
      return enableNewUIForZS ? '' : this.$t('billing.events.saas.capacityTips', [this.title]);
    },
    addAnotherCapacityCta() {
      return this.configPlanType === planTypes.EVENT_SAAS
        ? this.$t('billing.addAnother.events')
        : this.$t('billing.addAnother.sessions');
    },
    // override autoAddMixin
    mx_planType() {
      return this.configPlanType;
    },
    mx_addCapacityBtnTooltipDisabled() {
      const availablePlans = this.planList.filter(p => p.cartInfo.selected_number === 0);
      const availableMonthlyPlan = availablePlans.find(p => p.billing_cycle === BILLING_CYCLE.MONTHLY);
      if (!availableMonthlyPlan) return false;
      return true;
    },
    mx_addCapacityBtnTooltipContent() {
      if (!this.mx_addCapacityBtnTooltipDisabled) {
        return this.configPlanType === planTypes.EVENT_SAAS
          ? this.$t('billing.events.zoom_events_tooltip', [this.$t('billing.events.title_for_events')])
          : this.$t('billing.events.zoom_events_tooltip', [this.$t('billing.events.title_for_sessions')])
      }
      return '';
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan', 'batchRemovePlan']),
    addDefaultPlan() {
      const defaultCapacity = _.get(this.allCapacityOrderList, [0]);
      const { plan_code } = _.get(this.capacityMap, [defaultCapacity, 0], {});
      plan_code && this.updateSelectPlan({ plan_code, cartInfo: { selected_number: 1 } });
    },
    removePlans() {
      this.batchRemovePlan(this.selectedPlanList);
    },
    mx_getMaxHosts() {
      if (this.mx_canAutoAdd) {
        return this.mx_maxAutoAddHosts;
      }
      return this.basePlanTotalLicenses || 999;
    },
    mx_getIncreaseTips(capacity, license) {
      const currentCapacityPlanCount = getPlanTotalLicense(_.filter(this.selectedPlanList,(p) => p[this.configCapacityKey] === capacity)) || license;
      return currentCapacityPlanCount >= this.mx_getMaxHosts() ? this.$t('billing.licences_limit_tips', [this.title, this.$t(this.currentBasePlan.typeNameKey)]) : '';
    },
    mx_getMonthlyPlan(capacityPlanList = []) {
      const annualAdditionalPlan = _.find(this.getSelectedPlans, (p) => {
        return ZOOM_SASS_ADDITIONAL_CATEGORIES.includes(p.type) && p.billing_cycle === BILLING_CYCLE.ANNUAL;
      });

      const monthlyPlan = _.find(capacityPlanList, (p) => p.billing_cycle === BILLING_CYCLE.MONTHLY);

      const defaultDisabledBillingCycle = {
        disabled: true,
        disabledInnerDesc: this.$t('billing.events.onlyForAttendeesDesc', [100]),
      }

      if (monthlyPlan && annualAdditionalPlan) {
        return {
          plan: monthlyPlan,
          disabled: !!annualAdditionalPlan,
          tooltips: this.$t('billing.planform.monthly_as_base_disable_tooltip', [
            this.$t(getPlanTypeName(annualAdditionalPlan?.type)),
            this.title,
          ])
        }
      }

      if (enableNewUIForZS) {
        return { plan: monthlyPlan };
      }

      return { plan: monthlyPlan, ...(monthlyPlan ? {} : { plan: { billing_cycle: BILLING_CYCLE.MONTHLY }, ...defaultDisabledBillingCycle })  };
    },
    /**
     * plan license change sync base plan license
     * @param {number} newLicense newLicense
     * @param {number} oldLicense oldLicense
     * @param {object} planInfo
     * @param {number} planInfo.capacity
     * @param {string} planInfo.billingCycle
     */
    handleLicenseChange(newLicense, oldLicense, planInfo) {
      if (this.mx_canAutoAdd) {
        this.handleAutoAdd(newLicense, oldLicense, planInfo);
      }
    },
    /**
     * plan license change sync base plan license
     * @param {number} newLicense newLicense
     * @param {number} oldLicense oldLicense
     * @param {object} planInfo
     * @param {number} planInfo.capacity
     * @param {string} planInfo.billingCycle
     */
    handleAutoAdd(newLicense, oldLicense, { capacity }) {
      if (!this.handleAutoAddMap[capacity]) {
        const self = this;
        this.handleAutoAddMap[capacity] =  _.debounce((newLicense, oldLicense, { capacity }) => {
          // cancel switch plan, reset license
          self.mx_handlePlanLicenseChange(newLicense).catch((previousLicense) => {
            const configRowInstance = _.get(self.$refs, [`configRow${capacity}`, '0']);
            if (configRowInstance) {
              configRowInstance.changeLicense(previousLicense);
            }
          });
        }, 300);
      }
      this.handleAutoAddMap[capacity](newLicense, oldLicense, { capacity });
    }
  },
};

export default {
  mixins: [plansMixin, planCommonMixin],
  components: {
    ConfigConsumptionRow,
    ConfigConsumptionRowV2,
    ConfigBase: {
      mixins: [ConfigBase, planCommonMixin],
    },
    SaasCapacityConfigList,
  },
  data() {
    return {
      isExpanded: true,
      hasSelectedPlan: false,
      saasMode: '',
      saasModeList: [],
      EventModeMap,
    };
  },
  computed: {
    ...mapGetters(['planMapFlat', 'getTypeGroupSelectedPlans']),
    planList() {
      return this.planMapFlat.filter((p) => p.type === this.planType);
    },
    saasPlans() {
      return _.filter(this.planList, (p) => p.isSaas);
    },
    consumptionPlans() {
      return _.filter(this.planList, (p) => p.isPPA);
    },
    isSassPlansAvailable() {
      const not = this.saasPlans?.length === 0 || pageConfig.from === '';
      return !not;
    },
    configRowName() {
      if (enableNewUIForZS) {
        return 'CapacityConfigRowV2';
      }
      return 'CapacityConfigRow';
    },
    configConsumptionRowName() {
      if (enableNewUIForZS) {
        return 'ConfigConsumptionRowV2';
      }
      return 'ConfigConsumptionRow';
    },

    isShowRemoveBtn () {
      if(isFromShoppingCart()) {
        return isProductCanRemove(this.planList[0])
      }
      return false;
    }
  },
  methods: {
    ...mapMutations(['updateSelectPlan', 'batchRemovePlan']),
    initSaasMode() {
      this.saasModeList = [
        {
          label: EventModeMap.PAY_PER_ATTENDEE,
          name: this.$t('billing.events.ppa.title'),
        },
      ];

      if (this.isSassPlansAvailable) {
        this.saasModeList.push({
          label: EventModeMap.SUBSCRIPTION,
          name: this.$t('billing.events.saas.title'),
        });
      }

      const selectedPlans = this.getTypeGroupSelectedPlans[this.planType];
      const saasPlans = _.filter(selectedPlans, (p) => p.isSaas);
      const consumptionPlans = _.filter(selectedPlans, (p) => p.isPPA);

      if (saasPlans.length) {
        this.saasMode = EventModeMap.SUBSCRIPTION;
      } else if (consumptionPlans.length) {
        this.saasMode = EventModeMap.PAY_PER_ATTENDEE;
      }
    },
  },
  created() {
    this.initSaasMode();
  },
};
</script>
